function groupMatrixCustomersByRegion(customers = [], regions = []) {
  const result = new Map()
  for (const region of regions) {
    result.set(String(region.id), [])
  }
  for (const customer of customers) {
    const regionKey = String(customer.regionBucketId)
    if (!result.has(regionKey)) {
      result.set(regionKey, [])
    }
    result.get(regionKey).push(customer)
  }
  for (const list of result.values()) {
    list.sort((left, right) => (left.displayOrder || 0) - (right.displayOrder || 0))
  }
  return result
}

export function displayInvoiceCompanyName(customer) {
  return customer?.companyNameSnapshot ||
    customer?.companyName ||
    customer?.customerName ||
    customer?.name ||
    '-'
}

export function invoiceRegionTotalAmount(regionId, customers = []) {
  return customers
    .filter(customer => String(customer.regionBucketId) === String(regionId))
    .reduce((total, customer) => total + Number(customer.targetOutputAmount || 0), 0)
}

export function isFullProfitRateCustomer(customer) {
  const rate = Number(customer?.targetProfitRate)
  return Number.isFinite(rate) && rate === 1
}

export function buildRegionMatrixRows(regions = [], customers = [], minRows = 4) {
  const customersByRegion = groupMatrixCustomersByRegion(customers, regions)
  const maxCustomerCount = regions.reduce((max, region) => {
    const list = customersByRegion.get(String(region.id)) || []
    return Math.max(max, list.length)
  }, 0)
  const rowCount = Math.max(minRows, maxCustomerCount + 1)

  return Array.from({ length: rowCount }, (_, rowIndex) => ({
    rowIndex,
    cells: regions.map(region => {
      const list = customersByRegion.get(String(region.id)) || []
      const customer = list[rowIndex] || null
      return {
        key: customer?.id ? `customer-${customer.id}` : `empty-${region.id}-${rowIndex}`,
        region,
        rowIndex,
        customer
      }
    })
  }))
}

export function invoiceDropAnchor({
  sourceCustomerId,
  targetCustomers = [],
  droppedCustomer = null,
  position = null,
  rowIndex = null
} = {}) {
  const sourceKey = sourceCustomerId == null ? null : String(sourceCustomerId)
  const droppedCustomerId = droppedCustomer?.id
  if (droppedCustomerId != null && String(droppedCustomerId) !== sourceKey) {
    if (position === 'before') {
      return { beforePlanCustomerId: droppedCustomerId, afterPlanCustomerId: undefined }
    }
    if (position === 'after') {
      return { beforePlanCustomerId: undefined, afterPlanCustomerId: droppedCustomerId }
    }
  }

  if (position === 'empty') {
    const targetIndex = Number(rowIndex)
    const anchor = Number.isInteger(targetIndex) && targetIndex >= 0 ? targetCustomers[targetIndex] : null
    if (anchor?.id != null && String(anchor.id) !== sourceKey) {
      return { beforePlanCustomerId: anchor.id, afterPlanCustomerId: undefined }
    }
  }

  return { beforePlanCustomerId: undefined, afterPlanCustomerId: undefined }
}

export function invoiceDropMovePayload({
  sourceCustomer,
  targetRegionId,
  targetCustomers = [],
  droppedCustomer = null,
  position = null,
  rowIndex = null
} = {}) {
  if (!sourceCustomer?.id || targetRegionId == null) return null
  if (droppedCustomer?.id != null && String(droppedCustomer.id) === String(sourceCustomer.id)) return null
  const anchor = invoiceDropAnchor({
    sourceCustomerId: sourceCustomer.id,
    targetCustomers,
    droppedCustomer,
    position,
    rowIndex
  })
  const payload = {
    customer: sourceCustomer,
    planCustomerId: sourceCustomer.id,
    targetRegionId,
    beforePlanCustomerId: anchor.beforePlanCustomerId,
    afterPlanCustomerId: anchor.afterPlanCustomerId
  }
  return isSameDropPosition(sourceCustomer, targetCustomers, payload) ? null : payload
}

function isSameDropPosition(source, targetCustomers, payload) {
  if (String(source.regionBucketId) !== String(payload.targetRegionId)) return false
  const orderedIds = targetCustomers.map(customer => String(customer.id))
  const sourceIndex = orderedIds.indexOf(String(source.id))
  if (sourceIndex < 0) return false

  if (payload.beforePlanCustomerId != null) {
    const beforeIndex = orderedIds.indexOf(String(payload.beforePlanCustomerId))
    return beforeIndex === sourceIndex || beforeIndex === sourceIndex + 1
  }
  if (payload.afterPlanCustomerId != null) {
    const afterIndex = orderedIds.indexOf(String(payload.afterPlanCustomerId))
    return afterIndex === sourceIndex || afterIndex === sourceIndex - 1
  }
  return sourceIndex === orderedIds.length - 1
}

function sameText(left, right) {
  return String(left || '').trim() === String(right || '').trim()
}

export function isSameProfitRate(left, right) {
  return sameNumber(left, right, 0.00000001)
}

export function hasSameProductNames(left, right) {
  const normalize = value => Array.isArray(value)
    ? [...new Set(value.map(item => String(item || '').trim()).filter(Boolean))].sort()
    : []
  const leftNames = normalize(left)
  const rightNames = normalize(right)
  if (leftNames.length !== rightNames.length) return false
  return leftNames.every((name, index) => name === rightNames[index])
}

function sameNumber(left, right, tolerance = 0) {
  const leftNumber = Number(left)
  const rightNumber = Number(right)
  if (!Number.isFinite(leftNumber) || !Number.isFinite(rightNumber)) return false
  if (tolerance > 0) return Math.abs(leftNumber - rightNumber) <= tolerance
  return leftNumber === rightNumber
}

function matchesSubmittedCustomer(customer, submitted) {
  if (!submitted) return false
  const companyName = customer?.companyNameSnapshot || customer?.companyName || customer?.customerName || customer?.name
  const registeredRegion = customer?.registeredRegionSnapshot || customer?.registeredRegion
  return sameText(companyName, submitted.companyName) &&
    sameText(registeredRegion, submitted.registeredRegion) &&
    sameNumber(customer?.targetOutputAmount, submitted.targetOutputAmount) &&
    isSameProfitRate(customer?.targetProfitRate, submitted.targetProfitRate)
}

export function findNewPlanCustomer(beforeCustomers = [], afterCustomers = [], submitted = null) {
  const beforeIds = new Set(beforeCustomers.map(customer => String(customer.id)))
  const createdCustomers = afterCustomers.filter(customer => customer?.id != null && !beforeIds.has(String(customer.id)))
  if (submitted) {
    const matched = createdCustomers.find(customer => matchesSubmittedCustomer(customer, submitted))
    if (matched) return matched
  }
  return createdCustomers[0] || null
}
